import {
  EmployeeEnrollmentInfoQuery,
  cleanTelString,
  supportedLangToLanguagePref,
  useEmployeeEnrollmentInfoQuery,
  useLocaleState,
  useTrackingContext,
} from '@alice-financial/alice-sdk'
import {
  ApiRootException,
  GqlInputErrorException,
  composeMutationCallbacks,
  getGqlInputErrorHandler,
} from '@alice-financial/api'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { getServerErrorHandler } from '../../utils/forms/ServerError'
import { NonEditableRegisterInput, RegisterFormValues } from './types'
import { UseRegisterEmployeeOptions, useRegisterEmployee } from './useRegisterEmployee'

const getValues = (enrollmentData?: EmployeeEnrollmentInfoQuery): RegisterFormValues => {
  const user = enrollmentData?.currentUser
  const employee = enrollmentData?.employee

  // for registration, we populate the address with anything available from API, although it will be saved as the mailing address
  const { line1, line2, cityName, stateCode, zipcode } =
    employee?.mailingAddress || employee?.payrollAddress || {}
  return {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    preferredName: user?.preferredName ?? '',
    email: user?.email ?? '',
    tel: cleanTelString(user?.tel?.nationalNumber ?? ''),
    birthdate: user?.birthdate ?? '',
    acceptTermsAndEsign: Boolean(employee?.tosAcceptedAt),
    acceptAliceCardTos: Boolean(employee?.spendingConnectionInfo.hasAcceptedAliceCardTos),
    line1: line1 ?? '',
    line2,
    cityName: cityName ?? '',
    stateCode: stateCode ?? '',
    zipcode: zipcode ?? '',
  }
}

/**
 * This hook completes the registration step of the enrollment flow - it will
 * make the correct API calls to update the user or create the user
 */
export const useRegister = (
  nonEditableInput: NonEditableRegisterInput,
  mutationOptions?: UseRegisterEmployeeOptions
) => {
  const [language] = useLocaleState()
  const { data: enrollmentData } = useEmployeeEnrollmentInfoQuery()
  const values = getValues(enrollmentData)
  const { handleSubmit, ...form } = useForm<RegisterFormValues>({ values })
  const { setError } = form

  const serverErrorHandler = getServerErrorHandler(setError)
  const gqlInputErrorHandler = getGqlInputErrorHandler(setError, ['inviteCode', 'referredBy'])
  const _mutationOptions = composeMutationCallbacks(mutationOptions, {
    onError: (error) => {
      if (error instanceof GqlInputErrorException) {
        gqlInputErrorHandler(error)
        return
      }
      if (error instanceof ApiRootException) serverErrorHandler(error)
    },
  })

  const { mutate: registerEmployee, ...mutationState } = useRegisterEmployee(_mutationOptions)
  const [{ utmParams }] = useTrackingContext()

  const sendUserData = React.useCallback(
    ({ line1, line2, cityName, stateCode, zipcode, ...values }: RegisterFormValues) => {
      const input = {
        ...values,
        mailingAddress: {
          line1,
          line2,
          cityName,
          stateCode,
          zipcode,
        },
        language: supportedLangToLanguagePref(language),
        utmParams,
        ...nonEditableInput,
      }

      registerEmployee({ input })
    },
    [nonEditableInput, registerEmployee, utmParams, language]
  )

  return { onSubmit: handleSubmit(sendUserData), ...form, ...mutationState }
}
